import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, EmptyState, QueryChips } from '@teals/ui-web';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  useGetAnalyticsBreakdownQuery,
  useGetAnalyticsSessionQuery,
  useGetAnalyticsSessionsQuery,
  useGetAnalyticsSummaryQuery,
  useGetAnalyticsTimeseriesQuery,
} from '../../store/api/analyticsApi';
import AnalyticsReloadButton from './AnalyticsReloadButton';
import {
  formatChartBucket,
  formatPct,
  formatSessionId,
  formatTokens,
  isCostQuery,
  rangeIso,
} from './analyticsFormat';
import {
  AssistantAvatar,
  AssistantBubble,
  AssistantMessage,
  AssistantMessages,
  AssistantPanel,
  AssistantWelcome,
  AssistantWelcomeIcon,
  BreakdownTable,
  BudgetActions,
  BudgetHeader,
  BudgetRow,
  ChatInputArea,
  ChatInputWrap,
  ChatSendButton,
  ChatTextarea,
  ControlGroup,
  ControlLabel,
  HeaderActions,
  KpiCard,
  KpiChange,
  KpiLabel,
  KpiStrip,
  KpiValue,
  Overlay,
  PanelCard,
  PanelEmpty,
  PortletFull,
  PortletGrid,
  Progress,
  SectionSub,
  SectionTitle,
  SpotBody,
  SpotHeader,
  SpotMeta,
  SpotRow,
  SpotSlideOver,
  SpotTokens,
  SpotTop,
  TokenMixBar,
  TokenMixLegend,
  TokenMixSegment,
  TwoPanel,
} from './Analytics.styles';

export type ScopedAnalyticsScope = 'agent' | 'application';

export interface ScopedAnalyticsPanelProps {
  scope: ScopedAnalyticsScope;
  /** Display name for copy (agent name or application name). */
  scopeLabel: string;
  agentName?: string;
  applicationId?: string;
  onOpenPlayground?: () => void;
  onOpenConfiguration?: () => void;
}

interface AssistantMsg {
  id: string;
  text: React.ReactNode;
}

const AGENT_CHIPS = [
  'Why did tokens spike?',
  'Heaviest sessions this week',
  'Model mix by tokens',
  'Tokens in vs out trend',
];

const APP_CHIPS = [
  'Which agents used the most tokens?',
  'Tokens trend this month',
  'Heaviest sessions',
  'Model mix for this app',
  'Budget utilisation',
];

function buildWelcome(
  scope: ScopedAnalyticsScope,
  label: string,
  summary: {
    tokensTotal?: number;
    periodComparison?: { tokensTotalPct?: number | null };
    insightHints?: {
      topDriverAgent?: string | null;
      topDriverSharePct?: number | null;
      tokensTotalPct?: number | null;
    };
  } | undefined,
  isEmpty: boolean,
): React.ReactNode {
  if (isEmpty) {
    return scope === 'agent' ? (
      <>
        No usage for <strong>{label}</strong> in this range yet. Run Playground or
        wait for production traffic — costs stay on Payments.
      </>
    ) : (
      <>
        No usage for <strong>{label}</strong> yet. Associated agents will show in
        agent mix once harness traffic is recorded. Edit caps on Configuration;
        charges on Payments.
      </>
    );
  }
  const pct =
    summary?.insightHints?.tokensTotalPct ??
    summary?.periodComparison?.tokensTotalPct;
  const driver = summary?.insightHints?.topDriverAgent;
  const share = summary?.insightHints?.topDriverSharePct;
  const trend =
    pct == null ? (
      <>Token volume is ready to review</>
    ) : pct >= 0 ? (
      <>
        Token volume is <strong>up {pct}%</strong> vs prior period
      </>
    ) : (
      <>
        Token volume is <strong>down {Math.abs(pct)}%</strong> vs prior period
      </>
    );
  if (scope === 'application' && driver && share != null) {
    return (
      <>
        {trend}. <strong>{driver}</strong> drove <strong>{share}%</strong> of{' '}
        {label} tokens — open agent mix or spot analysis next. For dollar charges,
        see Payments.
      </>
    );
  }
  return (
    <>
      {trend}
      {driver ? (
        <>
          . Heaviest activity involves <strong>{driver}</strong>
        </>
      ) : null}
      . Open spot analysis for session detail — charges live on Payments.
    </>
  );
}

const ScopedAnalyticsPanel: React.FC<ScopedAnalyticsPanelProps> = ({
  scope,
  scopeLabel,
  agentName,
  applicationId,
  onOpenPlayground,
  onOpenConfiguration,
}) => {
  const navigate = useNavigate();
  const [rangeDays, setRangeDays] = useState(7);
  const [ask, setAsk] = useState('');
  const [spotSessionId, setSpotSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<AssistantMsg[]>([]);

  const args = useMemo(() => {
    const { from, to } = rangeIso(rangeDays);
    return {
      from,
      to,
      grain: 'day',
      agentName: scope === 'agent' ? agentName : undefined,
      applicationId: scope === 'application' ? applicationId : undefined,
    };
  }, [rangeDays, scope, agentName, applicationId]);

  const { data: summary, isLoading, refetch: refetchSummary } =
    useGetAnalyticsSummaryQuery(args);
  const { data: timeseries, refetch: refetchTimeseries } =
    useGetAnalyticsTimeseriesQuery({
      ...args,
      metric: 'tokens_total',
    });
  const { data: models, refetch: refetchModels } = useGetAnalyticsBreakdownQuery({
    ...args,
    dimension: 'model',
  });
  const { data: agents, refetch: refetchAgents } = useGetAnalyticsBreakdownQuery(
    { ...args, dimension: 'agent' },
    { skip: scope !== 'application' },
  );
  const { data: sessions, refetch: refetchSessions } = useGetAnalyticsSessionsQuery({
    ...args,
    limit: 8,
  });
  const { data: spotDetail } = useGetAnalyticsSessionQuery(spotSessionId || '', {
    skip: !spotSessionId,
  });

  const reloadAnalytics = () => {
    void refetchSummary();
    void refetchTimeseries();
    void refetchModels();
    if (scope === 'application') void refetchAgents();
    void refetchSessions();
  };
  const isEmpty =
    (summary?.tokensTotal ?? 0) === 0 && (summary?.sessions ?? 0) === 0;

  const chartData = (timeseries?.points || []).map((p) => ({
    bucket: p.bucket,
    tokens: p.value,
  }));
  const maxTokens = Math.max(0, ...chartData.map((d) => d.tokens));

  const tokensIn = summary?.tokensIn ?? 0;
  const tokensOut = (summary?.tokensOut ?? 0) + (summary?.reasoningTokens ?? 0);
  const mixTotal = tokensIn + tokensOut;
  const inPct = mixTotal > 0 ? Math.round((tokensIn / mixTotal) * 100) : 0;
  const outPct = mixTotal > 0 ? 100 - inPct : 0;

  const centerHref =
    scope === 'agent' && agentName
      ? `/analytics?agent=${encodeURIComponent(agentName)}&dimension=agent`
      : applicationId
        ? `/analytics?application=${encodeURIComponent(applicationId)}&dimension=application`
        : '/analytics';

  const chips = scope === 'agent' ? AGENT_CHIPS : APP_CHIPS;

  const pushAssistant = (text: React.ReactNode) => {
    setMessages((prev) => [
      ...prev,
      { id: `${Date.now()}-${prev.length}`, text },
    ]);
  };

  const seedWelcome = () => {
    if (messages.length > 0 || isLoading) return;
    pushAssistant(buildWelcome(scope, scopeLabel, summary, isEmpty));
  };

  React.useEffect(() => {
    if (!isLoading && messages.length === 0) {
      seedWelcome();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed once after first load
  }, [isLoading, summary?.tokensTotal]);

  const onAsk = (label: string) => {
    if (isCostQuery(label)) {
      pushAssistant(
        <>
          Analytics shows tokens and usage only. Open{' '}
          <strong>Payments</strong> for charges and invoices.
        </>,
      );
      navigate('/payments');
      return;
    }
    const lower = label.toLowerCase();
    if (lower.includes('week') || lower.includes('heaviest')) setRangeDays(7);
    if (lower.includes('month') || lower.includes('trend')) setRangeDays(30);
    if (lower.includes('budget')) {
      pushAssistant(
        <>
          Budget utilisation is shown in the portlets
          {scope === 'application'
            ? ' — edit this app’s cap on Configuration'
            : ''}
          . Organisation charges stay on Payments.
        </>,
      );
      return;
    }
    if (lower.includes('model')) {
      pushAssistant(
        <>
          Model mix by token volume is in the <strong>Model breakdown</strong>{' '}
          portlet for this {scope === 'agent' ? 'agent' : 'application'}.
        </>,
      );
      return;
    }
    if (lower.includes('agent')) {
      pushAssistant(
        <>
          Agent mix by tokens is in the <strong>Agent mix</strong> portlet.
          Open a row to inspect that agent’s Analytics.
        </>,
      );
      return;
    }
    if (lower.includes('spike')) {
      pushAssistant(
        buildWelcome(scope, scopeLabel, summary, isEmpty),
      );
      return;
    }
    pushAssistant(
      <>
        Showing tokens and sessions for <strong>{scopeLabel}</strong> in the
        selected range. Use chips or Open Center for org-wide compare.
      </>,
    );
  };

  const submitAsk = (e: React.FormEvent) => {
    e.preventDefault();
    const text = ask.trim();
    if (!text) return;
    onAsk(text);
    setAsk('');
  };

  const rangeButtons = [
    { d: 1, label: '24h' },
    { d: 7, label: '7d' },
    { d: 30, label: '30d' },
  ];

  return (
    <TwoPanel>
      <PanelCard>
        <AssistantPanel>
          <AssistantWelcome>
            <AssistantWelcomeIcon aria-hidden="true">✨</AssistantWelcomeIcon>
            <SectionTitle>Analytics Assistant</SectionTitle>
            <SectionSub>
              {scope === 'agent' ? (
                <>
                  Ask about <strong>{scopeLabel}</strong> tokens, sessions, and
                  models. Usage only — charges are on Payments; budget edits stay
                  on Applications.
                </>
              ) : (
                <>
                  Ask about <strong>{scopeLabel}</strong> tokens, associated
                  agents, and sessions. Usage only — charges are on Payments; edit
                  this app’s cap on Configuration.
                </>
              )}
            </SectionSub>
            <QueryChips
              chips={chips.map((label) => ({ label }))}
              onSelect={onAsk}
            />
          </AssistantWelcome>

          <AssistantMessages>
            {messages.map((m) => (
              <AssistantMessage key={m.id}>
                <AssistantAvatar>AI</AssistantAvatar>
                <AssistantBubble>{m.text}</AssistantBubble>
              </AssistantMessage>
            ))}
          </AssistantMessages>

          <SectionSub>
            <Link to={centerHref}>Open Analytics Center</Link>
            {' · '}
            <Link to="/payments">Payments</Link>
          </SectionSub>

          <ChatInputArea>
            <ChatInputWrap onSubmit={submitAsk}>
              <ChatTextarea
                rows={1}
                value={ask}
                onChange={(e) => setAsk(e.target.value)}
                placeholder={
                  scope === 'agent'
                    ? "Ask about this agent's tokens…"
                    : "Ask about this application's tokens…"
                }
                aria-label="Ask analytics assistant"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    submitAsk(e);
                  }
                }}
              />
              <ChatSendButton type="submit" aria-label="Send" disabled={!ask.trim()}>
                ↑
              </ChatSendButton>
            </ChatInputWrap>
          </ChatInputArea>
        </AssistantPanel>
      </PanelCard>

      <div>
        <ControlGroup style={{ marginBottom: '1rem', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap' }}>
            <ControlLabel>Range</ControlLabel>
            {rangeButtons.map((r) => (
              <Button
                key={r.d}
                size="sm"
                variant={rangeDays === r.d ? 'primary' : 'outline'}
                onClick={() => setRangeDays(r.d)}
              >
                {r.label}
              </Button>
            ))}
          </div>
          <HeaderActions>
            <AnalyticsReloadButton onReload={reloadAnalytics} />
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(centerHref)}
            >
              Open Center
            </Button>
          </HeaderActions>
        </ControlGroup>

        <SectionSub style={{ marginBottom: '1rem' }}>
          <strong>
            {scopeLabel} · last {rangeDays === 1 ? '24h' : `${rangeDays}d`}
          </strong>
        </SectionSub>

        {isLoading && !summary ? (
          <PanelCard>
            <SectionSub>Loading usage…</SectionSub>
          </PanelCard>
        ) : isEmpty ? (
          <PanelCard>
            <EmptyState
              title={
                scope === 'agent'
                  ? 'No usage for this agent yet'
                  : 'No usage for this application yet'
              }
              description={
                scope === 'agent'
                  ? 'Run a sandbox chat in Playground or wait for production traffic. Token KPIs and sessions will appear here.'
                  : 'Wait for production traffic from associated agents. Token KPIs, agent mix, and sessions will appear here.'
              }
              action={
                <>
                  {onOpenPlayground && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={onOpenPlayground}
                    >
                      Open Playground
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(centerHref)}
                  >
                    Analytics Center
                  </Button>
                </>
              }
            />
          </PanelCard>
        ) : (
          <PortletGrid>
            <PortletFull>
              <PanelCard>
                <SectionTitle>Key metrics</SectionTitle>
                <KpiStrip>
                  <KpiCard>
                    <KpiLabel>Sessions</KpiLabel>
                    <KpiValue>{formatTokens(summary?.sessions)}</KpiValue>
                    {formatPct(summary?.periodComparison?.sessionsPct) && (
                      <KpiChange
                        $tone={
                          (summary?.periodComparison?.sessionsPct || 0) >= 0
                            ? 'up'
                            : 'down'
                        }
                      >
                        {(summary?.periodComparison?.sessionsPct || 0) >= 0
                          ? '↑'
                          : '↓'}{' '}
                        {formatPct(summary?.periodComparison?.sessionsPct)}
                      </KpiChange>
                    )}
                  </KpiCard>
                  <KpiCard>
                    <KpiLabel>Tokens in</KpiLabel>
                    <KpiValue>{formatTokens(summary?.tokensIn)}</KpiValue>
                  </KpiCard>
                  <KpiCard>
                    <KpiLabel>Tokens out</KpiLabel>
                    <KpiValue>{formatTokens(summary?.tokensOut)}</KpiValue>
                  </KpiCard>
                  <KpiCard>
                    <KpiLabel>vs prior</KpiLabel>
                    <KpiValue>
                      {formatPct(summary?.periodComparison?.tokensTotalPct) ||
                        '—'}
                    </KpiValue>
                    {summary?.periodComparison?.tokensTotalPct != null && (
                      <KpiChange
                        $tone={
                          summary.periodComparison.tokensTotalPct >= 0
                            ? 'up'
                            : 'down'
                        }
                      >
                        Token volume
                      </KpiChange>
                    )}
                  </KpiCard>
                </KpiStrip>
              </PanelCard>
            </PortletFull>

            <PanelCard>
              <SectionTitle>Tokens over time</SectionTitle>
              <SectionSub>Day grain · click a bar for spot analysis</SectionSub>
              {chartData.length === 0 ? (
                <PanelEmpty>No timeseries points</PanelEmpty>
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart
                    data={chartData}
                    onClick={() => {
                      const sess = sessions?.sessions?.[0];
                      if (sess) setSpotSessionId(sess.sessionId);
                    }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="var(--border)"
                    />
                    <XAxis
                      dataKey="bucket"
                      hide={chartData.length > 14}
                      tickFormatter={(v) => formatChartBucket(String(v), 'day')}
                      minTickGap={28}
                      interval="preserveStartEnd"
                    />
                    <YAxis width={40} />
                    <Tooltip
                      labelFormatter={(label) =>
                        formatChartBucket(String(label), 'day')
                      }
                    />
                    <Bar dataKey="tokens" cursor="pointer">
                      {chartData.map((entry, i) => (
                        <Cell
                          key={`${entry.bucket}-${i}`}
                          fill={
                            entry.tokens === maxTokens && maxTokens > 0
                              ? 'var(--warning)'
                              : 'var(--accent)'
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </PanelCard>

            {scope === 'application' ? (
              <PanelCard>
                <SectionTitle>Agent mix</SectionTitle>
                <SectionSub>By token volume · this application</SectionSub>
                <BreakdownTable>
                  <thead>
                    <tr>
                      <th>Agent</th>
                      <th>Tokens</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(agents?.rows || []).map((r) => (
                      <tr key={r.key}>
                        <td>
                          <Link
                            to={`/agents/${encodeURIComponent(r.key)}?tab=analytics`}
                          >
                            {r.key}
                          </Link>
                        </td>
                        <td className="mono">{formatTokens(r.tokensTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </BreakdownTable>
                {!agents?.rows?.length && (
                  <PanelEmpty>No agent mix yet</PanelEmpty>
                )}
              </PanelCard>
            ) : (
              <PanelCard>
                <SectionTitle>Model breakdown</SectionTitle>
                <SectionSub>By token volume</SectionSub>
                <BreakdownTable>
                  <thead>
                    <tr>
                      <th>Model</th>
                      <th>Tokens</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(models?.rows || []).map((r) => (
                      <tr key={r.key}>
                        <td>{r.key}</td>
                        <td className="mono">{formatTokens(r.tokensTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </BreakdownTable>
                {!models?.rows?.length && (
                  <PanelEmpty>No model mix yet</PanelEmpty>
                )}
              </PanelCard>
            )}

            {scope === 'application' && (
              <PanelCard>
                <SectionTitle>Model breakdown</SectionTitle>
                <SectionSub>By token volume</SectionSub>
                <BreakdownTable>
                  <thead>
                    <tr>
                      <th>Model</th>
                      <th>Tokens</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(models?.rows || []).map((r) => (
                      <tr key={r.key}>
                        <td>{r.key}</td>
                        <td className="mono">{formatTokens(r.tokensTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </BreakdownTable>
                {!models?.rows?.length && (
                  <PanelEmpty>No model mix yet</PanelEmpty>
                )}
              </PanelCard>
            )}

            <PanelCard>
              <SectionTitle>Top sessions by tokens</SectionTitle>
              <BreakdownTable>
                <thead>
                  <tr>
                    <th>Session</th>
                    {scope === 'application' && <th>Agent</th>}
                    <th>Tokens</th>
                  </tr>
                </thead>
                <tbody>
                  {(sessions?.sessions || []).map((s) => (
                    <tr
                      key={s.sessionId}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSpotSessionId(s.sessionId)}
                    >
                      <td className="mono" title={s.sessionId}>
                        {formatSessionId(s.sessionId)}
                      </td>
                      {scope === 'application' && (
                        <td>{s.agentName || '—'}</td>
                      )}
                      <td className="mono">{formatTokens(s.tokensTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </BreakdownTable>
              {!sessions?.sessions?.length && (
                <PanelEmpty>No sessions yet</PanelEmpty>
              )}
            </PanelCard>

            {scope === 'agent' && (
              <PanelCard>
                <SectionTitle>Token mix</SectionTitle>
                <SectionSub>Input vs output + reasoning</SectionSub>
                <TokenMixBar>
                  <TokenMixSegment $pct={inPct} $tone="in" />
                  <TokenMixSegment $pct={outPct} $tone="out" />
                </TokenMixBar>
                <TokenMixLegend>
                  <span>Input {inPct}%</span>
                  <span>Output + reasoning {outPct}%</span>
                </TokenMixLegend>
              </PanelCard>
            )}

            {scope === 'application' && (
              <PanelCard>
                <SectionTitle>Budget utilisation</SectionTitle>
                <SectionSub>App cap % · edit on Configuration</SectionSub>
                {(() => {
                  const appBudget =
                    summary?.budgetUtilisation?.applications?.find(
                      (a) =>
                        a.applicationId === applicationId ||
                        a.name === scopeLabel,
                    ) || summary?.budgetUtilisation?.applications?.[0];
                  const pct = appBudget?.utilisationPct;
                  return (
                    <>
                      <BudgetRow>
                        <BudgetHeader>
                          <span>{scopeLabel} cap</span>
                          <strong>
                            {pct != null ? `${pct}% used` : 'No cap set'}
                          </strong>
                        </BudgetHeader>
                        <Progress $pct={pct || 0} />
                        <BudgetActions>
                          {onOpenConfiguration && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={onOpenConfiguration}
                            >
                              Adjust app budget
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate('/payments')}
                          >
                            View charges
                          </Button>
                        </BudgetActions>
                      </BudgetRow>
                    </>
                  );
                })()}
              </PanelCard>
            )}
          </PortletGrid>
        )}
      </div>

      {spotSessionId && (
        <>
          <Overlay onClick={() => setSpotSessionId(null)} />
          <SpotSlideOver aria-label="Spot analysis">
            <SpotHeader>
              <div>
                <strong>Session spot analysis</strong>
                <SpotMeta>{spotSessionId}</SpotMeta>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSpotSessionId(null)}
              >
                Close
              </Button>
            </SpotHeader>
            <SpotBody>
              <SpotRow>
                <SpotTop>
                  <span>Token mix</span>
                  <span>{formatTokens(spotDetail?.tokensTotal)} total</span>
                </SpotTop>
                <SpotTokens>
                  In {formatTokens(spotDetail?.tokensIn)} · Out{' '}
                  {formatTokens(spotDetail?.tokensOut)} · Reasoning{' '}
                  {formatTokens(spotDetail?.reasoningTokens)}
                </SpotTokens>
                <SpotMeta>
                  Models: {(spotDetail?.models || []).join(', ') || '—'}
                </SpotMeta>
              </SpotRow>
              {(spotDetail?.exchanges || []).slice(0, 5).map((ex) => (
                <SpotRow key={ex.exchangeId}>
                  <SpotTop>
                    <span>
                      {ex.exchangeId.slice(0, 10)}… · {ex.agentName}
                    </span>
                    <span>{formatTokens(ex.totalTokens)}</span>
                  </SpotTop>
                  <SpotMeta>
                    {ex.applicationId || '—'} · {ex.model}
                    {ex.createdAt ? ` · ${ex.createdAt}` : ''}
                  </SpotMeta>
                </SpotRow>
              ))}
              <SectionSub style={{ marginTop: '1.25rem' }}>
                Analytics shows token usage only. Charges at your negotiated rate
                are on Payments.
              </SectionSub>
              <BudgetActions>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate(centerHref)}
                >
                  Analytics Center
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/payments')}
                >
                  View charges
                </Button>
              </BudgetActions>
            </SpotBody>
          </SpotSlideOver>
        </>
      )}
    </TwoPanel>
  );
};

export default ScopedAnalyticsPanel;
